import React, { useState } from 'react';

const TEStatsTable = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({
    key: 'completion_rate',
    direction: 'descending'
  });

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Sort data
  const sortedData = React.useMemo(() => {
    if (!data) return [];
    
    const sortableData = [...data];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        const aValue = a[sortConfig.key] || 0;
        const bValue = b[sortConfig.key] || 0;
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [data, sortConfig]);

  // Get sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'ascending' ? ' Asc' : ' Desc';
  };

  // Performance indicator
  const getPerformanceIndicator = (rate) => {
    if (rate >= 90) return 'excellent';
    if (rate >= 75) return 'good';
    if (rate >= 50) return 'average';
    return 'poor';
  };

  if (!data || data.length === 0) {
    return (
      <div className="stats-table-container">
        <div className="no-data">
          <p>No TE performance data available for the selected period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stats-table-container">
      <div className="table-info">
        <p>Showing {sortedData.length} TEs</p>
      </div>
      
      <div className="table-wrapper">
        <table className="stats-table te-stats-table">
          <thead>
            <tr>
              <th 
                className="sortable"
                onClick={() => handleSort('te_name')}
              >
                TE Name{getSortIndicator('te_name')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('te_email')}
              >
                Email{getSortIndicator('te_email')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('assigned')}
              >
                Assigned{getSortIndicator('assigned')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('completed')}
              >
                Completed{getSortIndicator('completed')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('wip')}
              >
                WIP{getSortIndicator('wip')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('completion_rate')}
              >
                Completion Rate{getSortIndicator('completion_rate')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('total_word_count')}
              >
                Word Count{getSortIndicator('total_word_count')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('avg_turnaround_days')}
              >
                Avg Turnover{getSortIndicator('avg_turnaround_days')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('productivity_words_per_day')}
              >
                Productivity{getSortIndicator('productivity_words_per_day')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('daily_capacity')}
              >
                Daily Capacity{getSortIndicator('daily_capacity')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((te, index) => (
              <tr key={te.te_id} className="stats-row">
                <td className="name-cell">
                  <div className="te-name">{te.te_name}</div>
                </td>
                <td className="email-cell">
                  <div className="te-email">{te.te_email}</div>
                </td>
                <td className="number-cell">
                  <span className="stat-value assigned">{te.assigned}</span>
                </td>
                <td className="number-cell">
                  <span className="stat-value completed">{te.completed}</span>
                </td>
                <td className="number-cell">
                  <span className="stat-value wip">{te.wip}</span>
                </td>
                <td className="number-cell">
                  <span className={`stat-value rate ${getPerformanceIndicator(te.completion_rate)}`}>
                    {te.completion_rate}%
                  </span>
                </td>
                <td className="number-cell">
                  <span className="stat-value word-count">
                    {te.total_word_count.toLocaleString()}
                  </span>
                </td>
                <td className="number-cell">
                  <span className="stat-value turnaround">
                    {te.avg_turnaround_days}d
                  </span>
                </td>
                <td className="number-cell">
                  <span className="stat-value productivity">
                    {te.productivity_words_per_day.toLocaleString()}
                  </span>
                </td>
                <td className="number-cell">
                  <span className="stat-value capacity">
                    {te.daily_capacity}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-legend">
        <h4>Performance Indicators:</h4>
        <div className="legend-items">
          <div className="legend-item excellent">
            <span className="indicator"></span>
            <span>Excellent (90%+)</span>
          </div>
          <div className="legend-item good">
            <span className="indicator"></span>
            <span>Good (75-89%)</span>
          </div>
          <div className="legend-item average">
            <span className="indicator"></span>
            <span>Average (50-74%)</span>
          </div>
          <div className="legend-item poor">
            <span className="indicator"></span>
            <span>Poor (&lt;50%)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TEStatsTable;
